import { useEffect, useRef, useState } from "react";
import { fetchInvoiceMailAttachment } from "@/lib/invoiceMail";
import { getInvoiceApproval, type InvoiceApprovalPreview } from "@/lib/invoiceApproval";
import { isPdfFile, pickInvoiceAttachment } from "@/pages/invoiceApproval/invoicePreview";

let activeRenders = 0;
const waiters: Array<() => void> = [];

function acquireRenderSlot(): Promise<void> {
  if (activeRenders < 3) {
    activeRenders += 1;
    return Promise.resolve();
  }
  return new Promise((resolve) => {
    waiters.push(() => {
      activeRenders += 1;
      resolve();
    });
  });
}

function releaseRenderSlot(): void {
  activeRenders = Math.max(0, activeRenders - 1);
  const next = waiters.shift();
  if (next) next();
}

function isCancelledRender(err: unknown): boolean {
  if (!(err instanceof Error)) return false;
  return err.name === "AbortError" || err.name === "RenderingCancelledException";
}

export function InvoiceThumb({
  candidateId,
  preview,
  label,
}: {
  candidateId: number;
  preview?: InvoiceApprovalPreview | null;
  label: string;
}) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [imageUrl, setImageUrl] = useState<string | null>(null);
  const [ready, setReady] = useState(false);
  const [failed, setFailed] = useState(false);
  const [active, setActive] = useState<InvoiceApprovalPreview | null>(
    preview?.kind === "pdf" ? preview : null,
  );
  const [resolving, setResolving] = useState(preview?.kind !== "pdf");

  useEffect(() => {
    if (preview?.kind === "pdf" && preview.id) {
      setActive(preview);
      setResolving(false);
      return;
    }
    let cancelled = false;
    setResolving(true);
    setActive(null);
    void getInvoiceApproval(candidateId)
      .then((detail) => {
        if (cancelled) return;
        const best = pickInvoiceAttachment(detail.attachments);
        if (best?.id && isPdfFile(best)) {
          setActive({
            id: best.id,
            name: best.name,
            content_type: best.content_type,
            kind: "pdf",
          });
          return;
        }
        setActive(preview ?? null);
      })
      .catch(() => {
        if (!cancelled) setActive(preview ?? null);
      })
      .finally(() => {
        if (!cancelled) setResolving(false);
      });
    return () => {
      cancelled = true;
    };
  }, [candidateId, preview]);

  useEffect(() => {
    if (!active?.id) return;
    let cancelled = false;
    let revoked: string | null = null;
    let pdfDoc: { destroy: () => Promise<void> } | null = null;
    let renderTask: { cancel: () => void; promise: Promise<unknown> } | null = null;
    const controller = new AbortController();
    setFailed(false);
    setReady(false);
    setImageUrl(null);

    void (async () => {
      await acquireRenderSlot();
      try {
        if (cancelled) return;
        const blob = await fetchInvoiceMailAttachment(candidateId, active.id, controller.signal);
        if (cancelled) return;
        if (active.kind === "image") {
          revoked = URL.createObjectURL(blob);
          setImageUrl(revoked);
          setReady(true);
          return;
        }
        const pdfjs = await import("pdfjs-dist");
        const workerSrc = (await import("pdfjs-dist/build/pdf.worker.min.mjs?url")).default;
        pdfjs.GlobalWorkerOptions.workerSrc = workerSrc;
        if (cancelled) return;
        const data = new Uint8Array(await blob.arrayBuffer());
        const pdf = await pdfjs.getDocument({ data }).promise;
        if (cancelled) {
          await pdf.destroy().catch(() => undefined);
          return;
        }
        pdfDoc = pdf;
        const page = await pdf.getPage(1);
        if (cancelled) return;
        const canvas = canvasRef.current;
        if (!canvas) {
          setFailed(true);
          return;
        }
        const unscaled = page.getViewport({ scale: 1 });
        const viewport = page.getViewport({ scale: 360 / Math.max(unscaled.width, 1) });
        canvas.width = Math.floor(viewport.width);
        canvas.height = Math.floor(viewport.height);
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          setFailed(true);
          return;
        }
        const task = page.render({ canvasContext: ctx, viewport });
        renderTask = task;
        await task.promise;
        if (!cancelled) setReady(true);
      } catch (err) {
        if (cancelled || isCancelledRender(err)) return;
        setFailed(true);
      } finally {
        releaseRenderSlot();
      }
    })();

    return () => {
      cancelled = true;
      controller.abort();
      try {
        renderTask?.cancel();
      } catch {
        /* bereits beendet */
      }
      if (revoked) URL.revokeObjectURL(revoked);
      void pdfDoc?.destroy().catch(() => undefined);
    };
  }, [candidateId, active?.id, active?.kind]);

  if (resolving && active?.kind !== "pdf") {
    return <div className="h-full animate-pulse bg-fill-tertiary/50" />;
  }

  if (!active || failed) {
    return (
      <div className="flex h-full items-center justify-center bg-fill-tertiary/40 px-4 text-center text-xs text-label-tertiary">
        {failed ? "Vorschau nicht verfügbar" : "Kein Belegbild"}
      </div>
    );
  }

  if (active.kind === "image") {
    if (!imageUrl) {
      return <div className="h-full animate-pulse bg-fill-tertiary/50" />;
    }
    return <img src={imageUrl} alt={label} className="h-full w-full object-cover object-top" />;
  }

  return (
    <div className="relative h-full w-full bg-white">
      {ready ? null : <div className="absolute inset-0 animate-pulse bg-fill-tertiary/50" />}
      <canvas ref={canvasRef} className="h-full w-full object-cover object-top" aria-label={label} />
    </div>
  );
}
